import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline'
import { Box, Button, Chip, Stack, Typography, useTheme } from '@mui/material'
import React, { useState } from 'react'

interface VersionTypeSelectorProps {
  title: string
  dealPK: string
  dealSK: string
  dealName: string
  currentVersion: string
  onSubmit: (message: string) => void
  isInteractive: boolean
}

const VERSION_OPTIONS = [
  {
    type: 'major',
    label: 'Major Version',
    description: 'New agreement term with new dates, products, and pricing. A new PandaDoc contract will be required.',
    example: 'v1.0.0 → v2.0.0',
    color: '#D95F55',
  },
  {
    type: 'minor',
    label: 'Minor Version (Renewal)',
    description: 'Extend or adjust the agreement dates. Optionally create a new contract.',
    example: 'v1.0.0 → v1.1.0',
    color: '#F29112',
  },
  {
    type: 'patch',
    label: 'Patch Version',
    description: 'Pricing or quantity changes to existing products. No new contract needed.',
    example: 'v1.0.0 → v1.0.1',
    color: '#43A047',
  },
] as const

const VersionTypeSelector: React.FC<VersionTypeSelectorProps> = ({
  title,
  dealPK,
  dealSK,
  dealName,
  currentVersion,
  onSubmit,
  isInteractive,
}) => {
  const theme = useTheme()
  const [selected, setSelected] = useState<string | null>(null)
  const [submitted, setSubmitted] = useState(false)

  const handleSubmit = () => {
    if (!selected) return
    const option = VERSION_OPTIONS.find(o => o.type === selected)
    if (!option) return

    setSubmitted(true)
    onSubmit(
      `Create a ${selected} version change for "${dealName}" (PK: ${dealPK}, SK: ${dealSK}, current version: ${currentVersion}).`
    )
  }

  if (!isInteractive || submitted) {
    const chosenLabel = VERSION_OPTIONS.find(o => o.type === selected)?.label
    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          px: 1.5,
          py: 1,
          my: 0.5,
          borderRadius: 1.5,
          border: '1px solid',
          borderColor: 'divider',
          bgcolor: 'background.paper',
          maxWidth: 400,
          opacity: 0.8,
        }}
      >
        {submitted && <CheckCircleOutlineIcon sx={{ fontSize: 16, color: 'success.main' }} />}
        <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.75rem' }}>
          {submitted ? `Selected: ${chosenLabel}` : title}
        </Typography>
      </Box>
    )
  }

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 1.5,
        px: 1.5,
        py: 1.5,
        my: 0.5,
        borderRadius: 1.5,
        border: '1px solid',
        borderColor: 'divider',
        bgcolor: 'background.paper',
        maxWidth: 420,
      }}
    >
      <Box>
        <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.8rem' }}>
          {title}
        </Typography>
        <Typography variant="caption" sx={{ color: 'text.secondary' }}>
          {dealName} — {currentVersion}
        </Typography>
      </Box>

      <Stack gap={1}>
        {VERSION_OPTIONS.map(option => {
          const isSelected = selected === option.type
          return (
            <Box
              key={option.type}
              onClick={() => setSelected(option.type)}
              sx={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: 1.5,
                px: 1.5,
                py: 1,
                borderRadius: 1.5,
                border: '2px solid',
                borderColor: isSelected ? option.color : 'divider',
                bgcolor: isSelected
                  ? theme.palette.mode === 'dark'
                    ? `${option.color}15`
                    : `${option.color}10`
                  : 'transparent',
                cursor: 'pointer',
                transition: 'all 0.15s ease',
                '&:hover': {
                  borderColor: option.color,
                  bgcolor: theme.palette.mode === 'dark' ? `${option.color}10` : `${option.color}08`,
                },
              }}
            >
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.25 }}>
                  <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.8rem' }}>
                    {option.label}
                  </Typography>
                  <Chip
                    label={option.example}
                    size="small"
                    sx={{
                      height: 18,
                      fontSize: '0.65rem',
                      fontFamily: 'monospace',
                      bgcolor: `${option.color}20`,
                      color: option.color,
                      fontWeight: 600,
                    }}
                  />
                </Box>
                <Typography variant="caption" sx={{ color: 'text.secondary', lineHeight: 1.4 }}>
                  {option.description}
                </Typography>
              </Box>
            </Box>
          )
        })}
      </Stack>

      <Button
        size="small"
        variant="outlined"
        onClick={handleSubmit}
        disabled={!selected}
        sx={{
          alignSelf: 'flex-end',
          fontWeight: 500,
          fontSize: '0.75rem',
          borderColor: 'text.ai_agent_text',
          color: 'text.ai_agent_text',
          transition: 'all 0.3s ease',
          '&:hover': {
            transform: 'scale(1.05)',
            borderColor: 'text.highlight',
            color: 'text.primary',
          },
        }}
      >
        Continue
      </Button>
    </Box>
  )
}

export default VersionTypeSelector
